import { useState } from 'react';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkOrderField } from '@atom/types/work';
import { DETAIL_FIELD_WIDTH } from '@atom/utilities/taskUtilities';

interface Props {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  onBlur: (fieldId: string, value: any) => void;
  isDisabled: boolean;
  width?: string;
  requiredFieldIds?: Set<string>;
  requiredFieldName?: keyof WorkOrderField;
  isTemplate?: boolean;
  onClick?: () => void;
  error?: boolean;
  helperText?: string;
}

const WorkOrderTextField = ({
  field,
  onChange,
  onBlur,
  isDisabled,
  width = DETAIL_FIELD_WIDTH,
  requiredFieldIds = new Set([]),
  requiredFieldName,
  isTemplate,
  onClick,
  error,
  helperText,
}: Props) => {
  const [value, setValue] = useState<string>(field?.value || '');

  const handleChange = event => {
    setValue(event.target.value);
    onChange(field.id, event.target.value);
  };

  const handleOnBlur = event => {
    if (field?.value !== event.target.value) {
      setValue(event.target.value);
      onBlur(field.id, event.target.value);
    }
  };

  const style = {
    width,
  };

  const labelColor = requiredFieldIds.has(field.id)
    ? colors.brand.red
    : colors.neutral.dim;

  // template displays asterisk on required or requiredOnCreate
  const text =
    (isTemplate && (field.required || field.requiredOnCreate)) ||
    !!field[requiredFieldName]
      ? `* ${field?.title}`
      : field?.title;

  return (
    <div onClick={onClick}>
      <TextOverflowTooltip
        width={width}
        text={text}
        color={labelColor}
        fontSize={fonts.sm}
      />
      <TextField
        id={field.id}
        value={value}
        style={style}
        onChange={handleChange}
        onBlur={handleOnBlur}
        disabled={isDisabled}
        onClick={onClick}
        multiline
        error={error}
        helperText={helperText}
        data-cy={`workInfoField-${field.title}-TextInput`}
      />
    </div>
  );
};

export default WorkOrderTextField;
